import React from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import HealthRecordView from '../HealthRecordView';
import { classChildSelector, dateSelector } from '../selector';
import { savePageDraft, sendPage } from '../thunk';

import Confirm from './Confirm';
import ConfirmClose from './ConfirmClose';
import ConfirmDraft from './ConfirmDraft';

import type { HealthRecords } from '@/domain/childNotebook/component/HealthInfo';
import type { PageInputType } from '@/domain/childNotebook/component/PageFormInput';
import type { ChildJson } from '@/types/domain/child/entities/childEntity';
import type { FacilityJson } from '@/types/domain/childApply/entities/facilityEntity';
import type { PageJson } from '@/types/domain/childNotebook/entities/pageEntity';
import type { UserType } from '@/types/domain/user/redux';

import DialogTitle from '@/components/DialogTitle';
import DialogTransition from '@/components/DialogTransition';
import PageFormInput from '@/domain/childNotebook/component/PageFormInput';
import { uiSlice } from '@/domain/ui/redux';
import { toStringDate, toStringDateDisplay } from '@/utils/date';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

/** プロパティ */
export type Props = {
  /** ユーザー */
  user: UserType,
  /** 子ども */
  child: ChildJson,
  /** 施設 */
  facility: FacilityJson,
  /** 施設リスト */
  facilityList: FacilityJson[],
  /** 下書き */
  pageDraft: PageJson | undefined,
  /** 本日の健康記録 */
  healthRecords: HealthRecords,
  /** 閉じる */
  close: () => void,
};

const AddPage: React.FC<Props> = ({
  user,
  child,
  facility,
  facilityList,
  pageDraft,
  healthRecords,
  close,
}) => {
  const dispatch = useDispatch();
  const targetDate = useSelector(dateSelector);
  const classChildList = useSelector(classChildSelector);
  const classChild = classChildList.find(
    (v) => v.data.parentId === child.parentId && v.data.childId === child.childId,
  );
  const hideCondition = classChild?.data.hideCondition || false;
  const healthRecordTypes = classChild?.data.childNotebookContents || [];
  const date = toStringDate(new Date(targetDate));
  const [
    itemConfirm,
    setItemConfirm,
  ] = React.useState<{
    page: PageJson,
    child: ChildJson,
    attachments: File[],
  } | undefined>(undefined);
  const [
    itemConfirmDraft,
    setItemConfirmDraft,
  ] = React.useState<PageJson | undefined>(undefined);
  const [isOpenConfirmClose, setIsOpenConfirmClose] = React.useState<boolean>(false);
  const [attachments, setAttachments] = React.useState<File[]>([]);
  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm<PageInputType>({
    defaultValues: {
      moodType: pageDraft?.data.moodCategory || '良好',
      comment: pageDraft?.data.comment || '',
    },
  });
  const dateTime = new Date(date);
  const convertInputToJson = (input: PageInputType, isSend: boolean): PageJson => ({
    data: {
      parentId: child.parentId,
      childId: child.childId,
      recorderId: user.name,
      recorderType: 'app',
      recorderName: user.name,
      recordDate: targetDate,
      moodCategory: input.moodType,
      comment: input.comment,
      images: [],
      attachments: [],
      isSent: isSend,
    },
    pub: {
      sendId: `${child.parentId}_${child.childId}`,
      replyId: facility.data.facilityId,
      data: {
        alreadyRead: false,
      },
    },
  });
  const onSubmitSend = (input: PageInputType) => {
    // 送信時はコメント必須（下書きは任意）
    if (!input.comment || input.comment.trim().length === 0) {
      dispatch(uiSlice.actions.setMessage({ key: 'error', content: 'コメントを入力してください。' }));
      return;
    }
    setItemConfirm({
      page: convertInputToJson(input, true),
      child,
      attachments,
    });
  };
  const onSubmitDraft = (input: PageInputType) => {
    if (attachments.length > 0) {
      setItemConfirmDraft(convertInputToJson(input, false));
    } else {
      dispatch(savePageDraft(convertInputToJson(input, false)));
      close();
    }
  };
  return (
    <Dialog
      open
      onClose={() => setIsOpenConfirmClose(true)}
      TransitionComponent={DialogTransition}
      fullScreen
    >
      <DialogTitle title={toStringDateDisplay(dateTime, true)} />
      <div style={{ padding: '10px 24px 0px 24px' }}>
        <h3 style={{ margin: '1.5em 0 0 0' }}>健康記録</h3>
        <HealthRecordView
          user={user}
          child={child}
          facilityList={facilityList}
          healthRecords={healthRecords}
          healthRecordTypes={healthRecordTypes}
          date={date}
        />
      </div>
      <form onSubmit={handleSubmit(onSubmitSend)}>
        <DialogContent style={{ paddingTop: '0' }}>
          <h3 style={{ margin: '1.5em 0 0 0' }}>ご家庭入力欄</h3>
          <PageFormInput
            control={control}
            errors={errors}
            comment={watch('comment')}
            hideCondition={hideCondition}
          />
        </DialogContent>
        <div style={{ marginLeft: '24px' }}>
          <Button
            variant="contained"
            component="label"
            disabled={attachments.length >= 4}
          >
            写真
            <input
              type="file"
              accept="image/png, image/jpeg, image/heic"
              style={{ display: 'none' }}
              value=""
              onChange={(e) => {
                if (e.target.files && e.target.files?.length > 0) {
                  const newAttachment = [...attachments];
                  for (let i = 0; i < e.target.files.length; i += 1) {
                    const file = e.target.files[i];
                    if (file.size > MAX_FILE_SIZE) {
                      dispatch(uiSlice.actions.setMessage({ key: 'error', content: 'ファイルの容量は5MBまでにしてください。' }));
                    } else {
                      newAttachment.push(file);
                    }
                  }
                  setAttachments(newAttachment.slice(0, 4));
                }
              }}
              multiple
            />
          </Button>
          <Button
            variant="contained"
            component="label"
            disabled={attachments.length >= 4}
            style={{ marginLeft: '4px' }}
          >
            ファイル
            <input
              type="file"
              style={{ display: 'none' }}
              value=""
              onChange={(e) => {
                if (e.target.files && e.target.files?.length > 0) {
                  const newAttachment = [...attachments];
                  for (let i = 0; i < e.target.files.length; i += 1) {
                    const file = e.target.files[i];
                    if (file.size > MAX_FILE_SIZE) {
                      dispatch(uiSlice.actions.setMessage({ key: 'error', content: 'ファイルの容量は5MBまでにしてください。' }));
                    } else {
                      newAttachment.push(file);
                    }
                  }
                  setAttachments(newAttachment.slice(0, 4));
                }
              }}
              multiple
            />
          </Button>
          <span style={{ marginLeft: '10px' }}>
            添付ファイル（4つまで）
            {attachments.length > 0 && attachments.map((v, index) => (
              <div key={v.name}>
                <span>{v.name}</span>
                <IconButton
                  color="primary"
                  aria-label="delete file"
                  component="label"
                  onClick={() => {
                    const newAttachments = [...attachments];
                    newAttachments.splice(index, 1);
                    setAttachments(newAttachments);
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </div>
            ))}
          </span>
        </div>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit(onSubmitDraft)}
            startIcon={<SaveIcon />}
            disabled={pageDraft?.data.isSent}
          >
            下書き保存
          </Button>
          <Button
            variant="contained"
            type="submit"
            color="primary"
            startIcon={<SendIcon />}
          >
            送信する
          </Button>
          <Button
            variant="contained"
            onClick={() => setIsOpenConfirmClose(true)}
            color="primary"
          >
            閉じる
          </Button>
        </DialogActions>
      </form>
      {itemConfirm && (
        <Confirm
          pageApp={itemConfirm.page}
          hideCondition={hideCondition}
          attachments={itemConfirm.attachments}
          send={() => {
            dispatch(sendPage({
              data: itemConfirm.page,
              attachments: itemConfirm.attachments,
              facilityIds: facilityList.map((v) => v.data.facilityId),
              action: close,
            }));
            setItemConfirm(undefined);
          }}
          cancel={() => setItemConfirm(undefined)}
        />
      )}
      {itemConfirmDraft && (
        <ConfirmDraft
          save={() => {
            dispatch(savePageDraft(itemConfirmDraft));
            setItemConfirmDraft(undefined);
            close();
          }}
          cancel={() => setItemConfirmDraft(undefined)}
        />
      )}
      {isOpenConfirmClose && (
        <ConfirmClose
          close={() => close()}
          cancel={() => setIsOpenConfirmClose(false)}
        />
      )}
    </Dialog>
  );
};

export default AddPage;
